import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';

const BRAND_MARK = <img src="/dl-logo.png" alt="Digital Lotus" className="brand-mark" />;

const MIN_LEN = 10;

// Shared landing page for both password recovery links and new-user invite
// links.
//
// Preferred (next round, 2026-09-27): the email templates link here with
// ?token_hash=...&type=invite|recovery. We exchange it with verifyOtp, which
// works in any browser and survives link scanners (nothing is consumed until
// this page runs). The older PKCE / implicit links still work: Supabase JS
// (detectSessionInUrl) exchanges the code in the URL for a session on mount.
// Once the session is live, this page collects a password and calls
// updateUser({password}).
export default function SetPassword() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(false);
  const [verifyErr, setVerifyErr] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const tokenHash = params.get('token_hash');
    const type = params.get('type');
    if (!tokenHash || !type) return;
    let cancelled = false;
    (async () => {
      setVerifying(true);
      const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
      if (cancelled) return;
      setVerifying(false);
      if (error) setVerifyErr(error.message);
      // drop the one-time token from the address bar
      window.history.replaceState({}, '', '/set-password');
    })();
    return () => { cancelled = true; };
  }, []);

  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [err,      setErr]      = useState(null);
  const [pending,  setPending]  = useState(false);
  const [done,     setDone]     = useState(false);

  // If Supabase JS reports an auth error while parsing the URL (expired link,
  // etc.), the URL will contain error_description. Surface it plainly.
  const [urlErr, setUrlErr] = useState(null);
  useEffect(() => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const params = new URLSearchParams(
      hash.startsWith('#') ? hash.slice(1) : (search.startsWith('?') ? search.slice(1) : '')
    );
    const desc = params.get('error_description') || params.get('error');
    if (desc) setUrlErr(decodeURIComponent(desc.replace(/\+/g, ' ')));
  }, []);

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    if (password.length < MIN_LEN) {
      setErr(`Use at least ${MIN_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setErr('Those don\'t match.');
      return;
    }
    setPending(true);
    const { error } = await supabase.auth.updateUser({ password });
    setPending(false);
    if (error) { setErr(error.message); return; }
    setDone(true);
    // Give the AuthProvider effect a beat to re-fetch me + clients, then
    // send them to the landing page. The router will pick /needs-attention
    // once authenticated.
    setTimeout(() => navigate('/', { replace: true }), 900);
  }

  if (urlErr || verifyErr) {
    return (
      <div className="signin">
        {BRAND_MARK}
        <h1>This link isn't valid</h1>
        <p>{urlErr || verifyErr}</p>
        <p className="hint">
          <Link to="/reset">Request a new reset link</Link>
        </p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="signin">
        {BRAND_MARK}
        <h1>Setting up…</h1>
        <p>{verifying ? 'Verifying your link…' : 'One moment while we verify your link.'}</p>
        {!verifying && (
          <p className="hint">Still here after a few seconds? <Link to="/reset">Request a new link</Link>.</p>
        )}
      </div>
    );
  }

  if (done) {
    return (
      <div className="signin">
        {BRAND_MARK}
        <h1>Password saved</h1>
        <p>Signing you in…</p>
      </div>
    );
  }

  return (
    <div className="signin">
      {BRAND_MARK}
      <h1>Set your password</h1>
      <p>Pick something at least {MIN_LEN} characters. You'll use it to sign in from now on.</p>
      <form onSubmit={onSubmit}>
        <input
          type="password"
          placeholder="New password"
          required
          minLength={MIN_LEN}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={pending}
        />
        <input
          type="password"
          placeholder="Confirm password"
          required
          minLength={MIN_LEN}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={pending}
        />
        <button type="submit" disabled={pending || !password || !confirm}>
          {pending ? 'Saving…' : 'Save password'}
        </button>
        {err && <p className="msg err">{err}</p>}
      </form>
    </div>
  );
}
